<template>
  <div class="tw-container min-h-screen">
    <SimplifiedHeroNew
      :title="onboardingTextConfig.knowledgeBaseSimplifiedTitle"
      :subtitle="onboardingTextConfig.knowledgeBaseSimplifiedSubtitle"
      :action-button="heroActionButton"
      :dropdown-actions="categoryDropdownActions"
    />

    <!-- Filters -->
    <KnowledgeBaseFilters
      :categories="categories"
      :community-id="communityId"
      @search-changed="handleSearchChanged"
      @category-changed="handleCategoryChanged"
      @bookmarked-filter-changed="handleBookmarkedFilterChanged"
      @filters-cleared="handleFiltersCleared"
    />

    <!-- Deep Dive Reports (trial or paid subscribers only) -->
    <div v-if="currentView === 'overview' && canAccessTransitionReports && $store.getters.isFoodleap"
         class="mt-6 mb-4 px-6 pt-6 pb-8 rounded-2xl overflow-hidden relative"
         style="background: linear-gradient(160deg, #181b3e 0%, #21244A 40%, #1c2b1c 100%)">
      <!-- Ambient glows — mirrored from the report hero -->
      <div class="absolute top-0 right-0 w-80 h-80 rounded-full pointer-events-none"
           style="background: radial-gradient(circle, rgba(92,198,30,0.12) 0%, transparent 70%); filter: blur(60px); transform: translate(30%, -40%)"></div>
      <div class="absolute bottom-0 left-0 w-64 h-64 rounded-full pointer-events-none"
           style="background: radial-gradient(circle, rgba(112,193,255,0.08) 0%, transparent 70%); filter: blur(60px); transform: translate(-30%, 40%)"></div>

      <!-- Section header -->
      <div class="tw-flex items-center justify-between mb-5 relative">
        <div class="tw-flex items-center w-full gap-4">
          <div class="tw-flex items-center gap-2 whitespace-nowrap">
            <span class="material-symbols-outlined text-[18px]" style="color: #5CC61E">library_books</span>
            <h4 class="font-semibold text-white">Deep Dive Reports</h4>
          </div>
          <div class="h-px flex-grow" style="background: linear-gradient(to right, rgba(92,198,30,0.4), transparent)"></div>
          <span class="tw-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap border"
                style="background: rgba(92,198,30,0.12); color: #5CC61E; border-color: rgba(92,198,30,0.3)">
            <span class="material-symbols-outlined text-[12px]">workspace_premium</span>
            {{ $t('knowledge_base_deep_dive_exclusive_badge') }}
          </span>
        </div>
      </div>

      <!-- Report cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 relative">
        <component
          :is="report.route ? 'router-link' : 'div'"
          v-for="report in deepDriveReports"
          :key="report.id"
          v-bind="report.route ? { to: report.route } : {}"
          class="group tw-flex flex-col gap-3 rounded-xl p-5 transition-all no-underline border"
          :class="report.route ? 'cursor-pointer' : 'opacity-50 cursor-default'"
          style="background: rgba(255,255,255,0.05); border-color: rgba(255,255,255,0.1); backdrop-filter: blur(4px)"
          @mouseover="report.route && ($event.currentTarget.style.borderColor = 'rgba(92,198,30,0.5)')"
          @mouseleave="report.route && ($event.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)')"
        >
          <div class="w-10 h-10 rounded-lg tw-flex items-center justify-center flex-shrink-0"
               style="background: rgba(92,198,30,0.15)">
            <span class="material-symbols-outlined text-[20px]" style="color: #5CC61E">description</span>
          </div>
          <div class="tw-flex flex-col gap-1 flex-1">
            <p class="text-sm font-bold leading-snug transition-colors"
               style="color: rgba(255,255,255,0.95)">{{ report.title }}</p>
            <p class="text-xs font-medium leading-snug mt-0.5" style="color: #5CC61E">{{ report.subtitle }}</p>
            <p class="text-xs mt-1.5" style="color: rgba(255,255,255,0.3)">{{ report.date }}</p>
            <p class="text-xs leading-relaxed line-clamp-3 mt-1" style="color: rgba(255,255,255,0.5)">{{ report.description }}</p>
          </div>
          <div class="tw-flex items-center gap-1 text-xs font-semibold mt-auto pt-1"
               :style="report.route ? 'color: #5CC61E' : 'color: rgba(255,255,255,0.3)'">
            <span>{{ report.route ? $t('knowledge_base_deep_dive_read_report') : $t('knowledge_base_deep_dive_coming_soon') }}</span>
            <span v-if="report.route" class="material-symbols-outlined text-[14px] group-hover:translate-x-1 transition-transform">arrow_forward</span>
          </div>
        </component>

        <!-- Upsell card — trial users only -->
        <router-link
          v-if="hasTrialOnly"
          to="/transition-reports/trial"
          class="group tw-flex flex-col gap-3 rounded-xl p-5 transition-all no-underline border cursor-pointer"
          style="background: rgba(92,198,30,0.1); border-color: rgba(92,198,30,0.4); backdrop-filter: blur(4px)"
          @mouseover="$event.currentTarget.style.borderColor = 'rgba(92,198,30,0.7)'"
          @mouseleave="$event.currentTarget.style.borderColor = 'rgba(92,198,30,0.4)'"
        >
          <div class="w-10 h-10 rounded-lg tw-flex items-center justify-center flex-shrink-0"
               style="background: rgba(92,198,30,0.15)">
            <span class="material-symbols-outlined text-[20px]" style="color: #5CC61E">lock_open</span>
          </div>
          <div class="tw-flex flex-col gap-1 flex-1">
            <p class="text-sm font-bold leading-snug" style="color: rgba(255,255,255,0.95)">{{ $t('knowledge_base_deep_dive_unlock_title') }}</p>
            <p class="text-xs font-medium leading-snug mt-0.5" style="color: #5CC61E">{{ $t('knowledge_base_deep_dive_unlock_price') }}</p>
            <p class="text-xs mt-1.5" style="color: rgba(255,255,255,0.4)">{{ $t('knowledge_base_deep_dive_unlock_offer') }}</p>
            <p class="text-xs leading-relaxed mt-1" style="color: rgba(255,255,255,0.6)">
              {{ $t('knowledge_base_deep_dive_unlock_description') }}
            </p>
          </div>
          <div class="tw-flex items-center justify-center gap-1.5 text-xs font-bold mt-auto rounded-lg px-4 py-2.5 transition-all text-white group-hover:shadow-md"
               style="background: #5CC61E">
            <span>{{ $t('knowledge_base_deep_dive_subscribe_now') }}</span>
            <span class="material-symbols-outlined text-[14px] group-hover:translate-x-1 transition-transform">arrow_forward</span>
          </div>
        </router-link>
      </div>
    </div>

    <!-- Main Content -->
    <div class="pb-12 relative">
      <!-- Overview View -->
      <div v-if="currentView === 'overview'">
        <KnowledgeBaseContent
          :search-query="searchQuery"
          :category-filter="categoryFilter"
          :bookmarked-only="bookmarkedOnly"
          :community-id="communityId"
          :spotting-area-id="spottingAreaId"
          :is-edit-mode="isEditMode"
          @view-category="showCategoryGrid"
        />
      </div>

      <!-- Category Grid View -->
      <CategoryGridView
        v-else-if="currentView === 'category'"
        :category="selectedCategory"
        :categories="categories"
        :search-query="searchQuery"
        :community-id="communityId"
        :spotting-area-id="spottingAreaId"
        :bookmarked-only="bookmarkedOnly"
        :is-edit-mode="isEditMode"
        @go-back="goBackToOverview"
      />
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import UiMixin from '@/util/UiMixin'
  import TranslationsMixin from '@/util/TranslationsMixin'
  import Icon from '@/components/Icon/Icon.vue'
  import KnowledgeBaseContent from '@/Domain/KnowledgeBase/Components/KnowledgeBaseContent.vue'
  import CategoryGridView from '@/Domain/KnowledgeBase/Components/CategoryGridView.vue'
  import KnowledgeBaseFilters from '@/Domain/KnowledgeBase/Components/KnowledgeBaseFilters.vue'
  import { fetchCategories } from '@/api/knowledge-base'
  import DropdownActionsButton from '@/Domain/Shared/Components/DropdownActionsButton.vue'
  import { DropdownAction } from '@/Domain/Shared/shared-types'
  import { MUTATION_TYPES } from '@/store/modules/ui'
  import MODAL_IDS from '@/constants/modal-ids'
  import { fetchKnowledgeBaseCategories } from '@/Domain/KnowledgeBase/Api/knowledgeBase'
  import OnboardingMixin from '@/util/OnboardingMixin'
  import SimplifiedHeroNew from '@/components/SimplifiedHero/SimplifiedHeroNew.vue'

  export default defineComponent({
    name: 'KnowledgeBase',
    components: {
      SimplifiedHeroNew,
      DropdownActionsButton,
      CategoryGridView,
      KnowledgeBaseContent,
      KnowledgeBaseFilters,
      Icon
    },
    mixins: [UiMixin, TranslationsMixin, OnboardingMixin],
    data() {
      return {
        currentView: 'overview', // 'overview' or 'category'
        selectedCategory: null,
        categories: [],
        searchQuery: '',
        categoryFilter: null,
        bookmarkedOnly: false,
        realCategories: [], // Categories loaded from API
        customCategoryOrder: [],
        useCustomOrder: false,
        isLoadingCategories: false,
        isEditMode: false, // Edit mode for drag and drop
        deepDriveReports: [
          {
            id: 1,
            title: 'Balanced Protein',
            subtitle: 'Rebalancing health, affordability and sustainability in food',
            description: 'A Foodleap Deep Dive exploring how balanced protein is emerging as a practical transition pathway across Dutch retail and the wider food system.',
            date: 'Foodleap Deep Dive Report · May 2026',
            route: '/deep-dive-reports/balanced-protein-futures',
          },
        ],
      }
    },
    computed: {
      canAccessTransitionReports() {
        return this.$store.getters.canAccessTransitionReports
      },
      hasTrialOnly() {
        return this.$store.getters.hasActiveTransitionReportTrial && !this.$store.getters.hasActiveTransitionReportSubscription
      },
      canCreateKnowledgeBasePost() {
        // If we're in the spotting area context, check if the:
        // - user is a contributor to the spotting area
        // - or team member / owner
        // then it can create a KB post
        // The check "canUserUploadContent" only checks the general permissions which apply for all roles, except portfolio member
        // TODO: check what an ecosystem member can do in terms of linking spotting areas
        if (this.$store.getters.isMember) {
          return true
        }

        // If we're not in a spotting area context, use the general permission
        if (!this.spottingAreaId) {
          return this.$store.getters.canUserUploadContent
        }

        // If we are in a spotting area context and the user is a portfolio member, check if they contribute to this spotting area
        if (this.$store.getters.isPortfolioMember) {
          return this.$store.getters.contributeSpottingAreaIds.includes(Number(this.spottingAreaId))
        }

        return false
      },
      spottingAreaId(): Number | null {
        if (this.$route.params.spottingAreaId) {
          return Number(this.$route.params.spottingAreaId)
        }

        return null
      },
      communityId(): Number | null {
        if (this.$route.params.communityId) {
          return Number(this.$route.params.communityId)
        }

        return null
      },
      heroActionButton() {
        return this.canCreateKnowledgeBasePost && {
          label: this.$t('knowledge_base_add_post'),
          icon: 'plus',
          variant: 'rounded',
          onClick: () => this.navigateToCreate()
        }
      },
      categoryDropdownActions(): DropdownAction[] {
        return [
          {
            onClick: () => {
              this.openAddCategoryModal()
            },
            label: this.$t('knowledge_base_add_category'),
            icon: 'plus',
            analyticsId: 'knowledgeBase.addCategory'
          },
          {
            onClick: () => {
              this.openReorderCategoriesModal()
            },
            label: this.$t('knowledge_base_reorder_categories'),
            icon: 'sort',
            analyticsId: 'knowledgeBase.reorderCategory'
          },
          (this.isOwner || this.isMember) && {
            onClick: () => {
              this.toggleEditMode()
            },
            label: this.isEditMode ? this.$t('knowledge_base_exit_edit_mode') : this.$t('knowledge_base_enter_edit_mode'),
            icon: this.isEditMode ? 'x' : 'edit',
            analyticsId: 'knowledgeBase.toggleEditMode'
          },
        ].filter(Boolean)
      },
    },
    async mounted() {
      // Load categories for the grid view
      await this.loadRealCategories()
      await this.loadCategories()
    },
    methods: {
      openAddCategoryModal() {
        this.$store.commit(MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_KNOWLEDGE_BASE_CATEGORY)
      },
      openReorderCategoriesModal() {
        // Pass current categories to the modal for reordering
        // Modal will save changes to database when user confirms
        const modalContext = {
          categories: this.categories.map(cat => ({ id: cat.id, name: cat.name })),
          currentOrder: this.customCategoryOrder,
          useCustomOrder: this.useCustomOrder
        }
        this.$store.commit(MUTATION_TYPES.SET_MODAL_CONTEXT, modalContext)
        this.$store.commit(MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.REORDER_CATEGORIES)
      },
      async loadRealCategories() {
        this.isLoadingCategories = true
        try {
          await fetchKnowledgeBaseCategories()
            .then((response) => {
              // Categories are already ordered by the backend (by 'order' field, then by 'name')
              this.realCategories = response.data

              // Set up the category order based on the backend ordering
              if (this.realCategories && this.realCategories.length > 0) {
                // Categories are already ordered by the backend, so preserve that order
                this.customCategoryOrder = this.realCategories.map(cat => cat.id)
                this.useCustomOrder = true
              }
            })

        } catch (error) {
          console.error('Failed to load categories:', error)
        } finally {
          this.isLoadingCategories = false
        }
      },
      async loadCategories() {
        try {
          const response = await fetchCategories()
          this.categories = response.data
        } catch (error) {
          console.error('Failed to load categories:', error)
        }
      },

      navigateToCreate() {
        // Navigate to knowledge base create page
        if (this.$route.params.communityId) {
          this.$router.push(`/communities/${this.$route.params.communityId}/knowledge-base/create`)
        } else if (this.$route.params.spottingAreaId) {
          this.$router.push(`/spotting-areas/${this.$route.params.spottingAreaId}/knowledge-base/create`)
        } else {
          this.$router.push('/knowledge-base/create')
        }
      },

      showCategoryGrid(category) {
        this.selectedCategory = category
        this.currentView = 'category'
      },

      goBackToOverview() {
        this.currentView = 'overview'
        this.selectedCategory = null

        this.$nextTick(() => {
          // Clear the category filter when going back to overview
          this.categoryFilter = null
        })
      },

      handleSearchChanged(searchQuery) {
        this.searchQuery = searchQuery
      },

      handleCategoryChanged(categoryId) {
        // Instead of filtering, open the grid view for the selected category
        if (categoryId === null) {
          // If "All Categories" is selected, go back to overview
          this.goBackToOverview()
          return
        }

        if (categoryId === 'uncategorized') {
          // Handle uncategorized posts - pass null to showCategoryGrid
          this.showCategoryGrid(null)
          return
        }

        // Find the category object by ID
        const category = this.categories.find(cat => cat.id === categoryId)
        if (category) {
          this.showCategoryGrid(category)
        }
      },

      handleBookmarkedFilterChanged(showBookmarkedOnly) {
        this.bookmarkedOnly = showBookmarkedOnly
      },

      handleFiltersCleared() {
        this.searchQuery = ''
        this.categoryFilter = null
        this.bookmarkedOnly = false
      },

      toggleEditMode() {
        this.isEditMode = !this.isEditMode
      },

    }
  })
</script>
